import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { AppBar, Button, CircularProgress, Tab, Tabs, useMediaQuery } from '@material-ui/core';
import { createPortal } from 'react-dom';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import SwipeableViews from 'react-swipeable-views';

import {
  addInfluencerNetworks,
  addInventories,
  selectInfluencerNetwork,
} from 'features/influencerSetupSlice';
import { getRequest, postRequest } from 'utils/api';
import { setProfile } from 'features/profilesSlice';
import { openModal } from 'features/utilsSlice';
import useIsLoggedIn from 'hooks/useIsLoggedIn';
import useProfileOwner from 'hooks/useProfileOwner';
import useCurrentProfile from 'hooks/useCurrentProfile';
import FilterInventory from './FilterInventory';
import Inventory from './Inventory';
import toast from 'utils/toast';
import IconMainContainer from 'components/IconMainContainer';
import CartButton from 'components/cart/CartButton';

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: '#f8f9fb',
    borderBottom: '1px solid rgba(0, 0, 0, 0.15)',
  },

  textColorPrimary: {
    textTransform: 'capitalize',
    fontFamily: 'Avenir Roman',
    fontSize: 15,
    overflow: 'unset !important',

    [theme.breakpoints.up('sm')]: {
      minWidth: 120,
    },
  },

  colorPrimary: {
    borderBottom: 0,
    backgroundColor: '#f8f9fb',
  },

  color: {
    borderBottom: 0,
    backgroundColor: '#f8f9fb',
  },
}));

const btnStyle = { margin: '20px auto', display: 'flex', justifyContent: 'center' };
const alertStyle = { margin: 15 };

const TabPanel = ({ children, value, index, ...other }) => (
  <div role="tabpanel" hidden={value !== index} {...other}>
    {value === index && <div>{children}</div>}
  </div>
);

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

const InventoryTabs = ({
  uniqInfluencerNetworks,
  selectedNetworkIds,
  networks,
  inventories,
  profile,
  preview = false,
  isDialog,
}) => {
  const router = useRouter();
  const fan = Object.keys(router.query).includes('fan');
  const [value, setValue] = useState(fan ? 1 : 0);
  const [domReady, setDomReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const currentProfile = useCurrentProfile();
  const cartItems = useSelector(state => state.cart.cart_items);

  const classes = useStyles();
  const dispatch = useDispatch();
  const theme = useTheme();

  const profileOwner = useProfileOwner(profile);
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const isLoggedIn = useIsLoggedIn();

  const { username } = router.query;
  const isOwnProfilePage = username === currentProfile?.username;

  const fanInventories = inventories.filter(inventory => inventory.inventory_space.fan);
  const fanInventoryIds = fanInventories.map(inventory => inventory.id);
  const regularInventories = inventories.filter(inventory => !inventory.inventory_space.fan);

  const handleChange = (_event, newValue) => setValue(newValue);

  const handleChangeIndex = index => setValue(index);

  const fetchInventories = useCallback(() => {
    if (!profile?.id) return;

    getRequest({
      endpoint: 'inventories',
      query: { profile_id: profile.id, per_page: 200 },
    })
      .then(inventories => {
        dispatch(addInventories(inventories));
        dispatch(setProfile({ ...profile, inventories }));
      })
      .catch(() => {});
  }, [dispatch, profile]);

  const handleSubmit = async () => {
    try {
      const networks = await getRequest({
        endpoint: 'networks',
      });

      const imageAndLikeness = networks.find(network => network.name === 'Image & Likeness');

      const imageAndLikenessInfluencerNetwork = inventories.find(
        inventory => inventory.influencer_network.network_id === imageAndLikeness.id
      )?.influencer_network;

      if (imageAndLikenessInfluencerNetwork && fanInventories.length === 0) {
        dispatch(selectInfluencerNetwork(imageAndLikenessInfluencerNetwork));

        dispatch(
          openModal({
            name: 'Inventory Edit',
            data: { step: 'Inventory', fanOnly: true },
          })
        );
      } else {
        setIsLoading(true);

        const influencerNetworks = await getRequest({
          endpoint: 'influencer_networks',
          query: { profile_id: profile.id },
        });

        const response = await postRequest({
          endpoint: `influencer_networks/create_many`,
          data: {
            fan: true,
            profile_id: profile.id,
            network_ids: influencerNetworks
              .map(network => network.network_id)
              .concat(imageAndLikeness.id),
          },
        });

        dispatch(addInfluencerNetworks(response.data));
        fetchInventories();
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);

      toast(
        'error',
        'There was an issue setting up your fan inventory. Please try again or contact support',
        {
          icon: <IconMainContainer src="/icons/notifications/times.svg" alt="Times" errorStyle />,
        }
      );
    }
  };

  useEffect(() => setDomReady(true), []);

  useEffect(() => {
    fanInventoryIds.includes(+router.query.i) && setValue(1);
  }, [fanInventoryIds, router.query.i]);

  return (
    <>
      <AppBar position="static" color="default" className={classes.colorPrimary}>
        <Tabs
          value={value}
          onChange={handleChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          className={classes.root}
          style={isDesktop && preview ? { backgroundColor: '#fff' } : {}}
        >
          <Tab
            label={`${isLoggedIn && profileOwner && !preview ? 'My Promo' : 'Promo'}`}
            className={classes.textColorPrimary}
            disableRipple
          />

          <Tab label="Fan Items" className={classes.textColorPrimary} disableRipple />
        </Tabs>
      </AppBar>

      <SwipeableViews
        axis={theme.direction === 'rtl' ? 'x-reverse' : 'x'}
        index={value}
        onChangeIndex={handleChangeIndex}
        slideStyle={{ overflow: 'unset' }}
        containerStyle={{
          transition: 'transform 0.35s cubic-bezier(0.15, 0.3, 0.25, 1) 0s',
        }}
      >
        <TabPanel value={value} index={0}>
          {!isDialog && (
            <FilterInventory
              uniqInfluencerNetworks={uniqInfluencerNetworks}
              inventories={inventories}
              networks={networks}
            />
          )}

          <Inventory
            inventories={regularInventories}
            profile={profile}
            profilePreview={preview}
            selectedNetworkIds={selectedNetworkIds}
            isDialog={isDialog}
          />
        </TabPanel>

        <TabPanel value={value} index={1}>
          <Inventory
            fan
            inventories={fanInventories}
            profile={profile}
            profilePreview={preview}
            isDialog={isDialog}
          />

          {fanInventories.length === 0 && (
            <div>
              <Alert severity="info" style={alertStyle}>
                {profileOwner
                  ? 'You have no fan inventory'
                  : 'This influencer has no fan inventory right now.'}
              </Alert>

              {profileOwner && (
                <Button
                  onClick={handleSubmit}
                  disabled={isLoading}
                  color="primary"
                  variant="contained"
                  style={btnStyle}
                >
                  {isLoading ? <CircularProgress color="primary" /> : 'Setup fan inventory'}
                </Button>
              )}
            </div>
          )}
        </TabPanel>
      </SwipeableViews>

      {domReady && cartItems?.length > 0 && !isOwnProfilePage && (
        <>
          {createPortal(
            <CartButton />,

            document.getElementById('cart-button-portal')
          )}
        </>
      )}
    </>
  );
};

export default InventoryTabs;
